import React, { useEffect, useMemo, useState } from 'react';
import { FiX, FiChevronDown, FiChevronRight, FiCheck, FiSearch } from 'react-icons/fi';
import { getCaseProfile } from '../../data/caseProfiles';
import { apiService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import './LabReports.css';

const clinicalLabels = {
    laboratory: { en: 'Laboratory tests', ru: 'Лабораторные исследования', uz: 'Laboratoriya tahlillari' },
    imaging: { en: 'Imaging', ru: 'Инструментальные исследования', uz: 'Instrumental tekshiruvlar' },
    dynamic: { en: 'Dynamic testing', ru: 'Динамические пробы', uz: 'Dinamik tekshiruvlar' },
    urinalysis: { en: 'Urinalysis', ru: 'Анализ мочи', uz: 'Siydik tahlili' },
    biochemistry: { en: 'Biochemistry', ru: 'Биохимия', uz: 'Biokimyo' },
    glycemia: { en: 'Glycemia', ru: 'Гликемия', uz: 'Glikemiya' },
    electrolytes: { en: 'Electrolytes', ru: 'Электролиты', uz: 'Elektrolitlar' },
    renal: { en: 'Renal function', ru: 'Функция почек', uz: 'Buyrak faoliyati' },
    liver: { en: 'Liver function', ru: 'Функция печени', uz: 'Jigar faoliyati' },
    lipids: { en: 'Lipid profile', ru: 'Липидный профиль', uz: 'Lipid profili' },
    fbc: { en: 'Full blood count', ru: 'Общий анализ крови', uz: 'Umumiy qon tahlili' },
    glucose: { en: 'Glucose', ru: 'Глюкоза', uz: 'Glyukoza' },
    capillary_glucose: { en: 'Capillary glucose', ru: 'Капиллярная глюкоза', uz: 'Kapillyar glyukoza' },
    hba1c: { en: 'HbA1c', ru: 'HbA1c', uz: 'HbA1c' },
    ketones: { en: 'Ketones', ru: 'Кетоны', uz: 'Ketonlar' },
    blood_ketones: { en: 'Blood ketones', ru: 'Кетоны крови', uz: 'Qon ketonlari' },
    protein: { en: 'Protein', ru: 'Белок', uz: 'Oqsil' },
    nitrites: { en: 'Nitrites', ru: 'Нитриты', uz: 'Nitritlar' },
    potassium: { en: 'Potassium', ru: 'Калий', uz: 'Kaliy' },
    sodium: { en: 'Sodium', ru: 'Натрий', uz: 'Natriy' },
    urea: { en: 'Urea', ru: 'Мочевина', uz: 'Mochevina' },
    creatinine: { en: 'Creatinine', ru: 'Креатинин', uz: 'Kreatinin' },
    haemoglobin: { en: 'Haemoglobin', ru: 'Гемоглобин', uz: 'Gemoglobin' },
    wbc: { en: 'White blood cells', ru: 'Лейкоциты', uz: 'Leykotsitlar' },
    platelets: { en: 'Platelets', ru: 'Тромбоциты', uz: 'Trombotsitlar' },
    ecg: { en: 'ECG', ru: 'ЭКГ', uz: 'EKG' },
    chest_xray: { en: 'Chest X-ray', ru: 'Рентген грудной клетки', uz: 'Ko‘krak qafasi rentgeni' },
    ultrasound: { en: 'Ultrasound', ru: 'УЗИ', uz: 'UTT' },
    ct_mri: { en: 'CT / MRI', ru: 'КТ / МРТ', uz: 'KT / MRT' },
    antibodies: { en: 'Antibodies', ru: 'Антитела', uz: 'Antitanalar' },
    gad_antibodies: { en: 'GAD antibodies', ru: 'Антитела GAD', uz: 'GAD antitanalari' },
    anion_gap: { en: 'Anion gap', ru: 'Анионная разница', uz: 'Anion oralig‘i' },
    abg_ph: { en: 'Arterial blood gas pH', ru: 'pH артериальной крови', uz: 'Arterial qon pH ko‘rsatkichi' }
};

const labelAliases = {
    laboratory: ['lab reports', 'laboratory tests', 'laboratory', 'анализы', 'лабораторные исследования', 'tahlillar', 'laboratoriya'],
    imaging: ['imaging', 'instrumental studies', 'инструментальные', 'инструментальные исследования', 'instrumental tekshiruvlar'],
    dynamic: ['dynamic testing', 'dynamic_testing', 'динамические пробы', 'dinamik tekshiruvlar'],
    urinalysis: ['urinalysis', 'urine', 'ur', 'ua', 'анализ мочи', 'моча', 'siydik tahlili', 'siydik'], biochemistry: ['biochemistry', 'биохимия', 'biokimyo'],
    glycemia: ['glycemia', 'гликемия', 'glikemiya'], electrolytes: ['electrolytes', 'электролиты', 'elektrolitlar'],
    renal: ['renal function', 'renal_function', 'почки и печень', 'функция почек', 'buyrak faoliyati'],
    liver: ['liver function', 'liver_function', 'функция печени', 'jigar faoliyati'],
    lipids: ['lipids', 'lipid profile', 'lipid_profile', 'липидный профиль', 'lipid profili'],
    fbc: ['fbc', 'оак', 'full blood count', 'общий анализ крови', 'umumiy qon tahlili'],
    glucose: ['glucose', 'глюкоза', 'glyukoza'], capillary_glucose: ['capillary glucose', 'capillary_glucose', 'капиллярная глюкоза', 'kapillyar glyukoza'],
    hba1c: ['hba1c', 'гликированный гемоглобин', 'glikirlangan gemoglobin'], ketones: ['ketones', 'ketonlar', 'кетоны', 'ацетон'],
    blood_ketones: ['blood ketones', 'blood_ketones', 'кетоны крови', 'qon ketonlari'], protein: ['protein', 'белок', 'oqsil'],
    nitrites: ['nitrites', 'нитриты', 'nitritlar'], potassium: ['potassium', 'калий', 'kaliy'], sodium: ['sodium', 'натрий', 'natriy'],
    urea: ['urea', 'мочевина', 'mochevina'], creatinine: ['creatinine', 'креатинин', 'kreatinin'],
    haemoglobin: ['haemoglobin', 'hemoglobin', 'гемоглобин', 'gemoglobin', 'hb'], wbc: ['wbc', 'white blood cells', 'лейкоциты', 'leykotsitlar'],
    platelets: ['platelets', 'тромбоциты', 'trombotsitlar'], ecg: ['ecg', 'ekg', 'экг', 'кардиограмма'],
    chest_xray: ['chest xray', 'chest x ray', 'chest_xray', 'рентген грудной клетки', 'ko krak qafasi rentgeni'],
    ultrasound: ['ultrasound', 'узи', 'utt'], ct_mri: ['ct mri', 'ct_mri', 'кт', 'мрт', 'kt', 'mrt'],
    antibodies: ['antibodies', 'антитела', 'antitanalar'], gad_antibodies: ['gad antibodies', 'gad_antibodies', 'антитела gad', 'gad antitanalari'],
    anion_gap: ['anion gap', 'anion_gap', 'анионная разница', 'анионный промежуток', 'anion oraligi'],
    abg_ph: ['abg ph', 'abg_ph', 'blood gas ph', 'газы крови', 'кщс', 'qon gazlari', 'qon ph']
};

const searchGroups = [
    ['glucose', 'blood glucose', 'glycemia', 'sugar', 'глюкоза', 'глюкоза крови', 'гликемия', 'сахар', 'glyukoza', 'qon glyukozasi', 'qondagi glyukoza', 'glikemiya', 'shakar'],
    ['ketones', 'ketone', 'acetone', 'кетоны', 'кетон', 'ацетон', 'ketonlar', 'keton', 'aseton'],
    ['urinalysis', 'urine', 'ur', 'ua', 'анализ мочи', 'моча', 'siydik tahlili', 'siydik'],
    ['abg', 'blood gas', 'acid base', 'ph', 'кщс', 'газы крови', 'qon gazlari', 'qon ph'],
    ['anion gap', 'анионная разница', 'анионный промежуток', 'anion oraligi'],
    ['electrolytes', 'potassium', 'sodium', 'электролиты', 'калий', 'натрий', 'elektrolitlar', 'kaliy', 'natriy'],
    ['fbc', 'cbc', 'blood count', 'оак', 'общий анализ крови', 'umumiy qon tahlili'],
    ['haemoglobin', 'hemoglobin', 'hb', 'гемоглобин', 'gemoglobin'], ['wbc', 'white blood cells', 'лейкоциты', 'leykotsitlar'],
    ['platelets', 'тромбоциты', 'trombotsitlar'],
    ['renal', 'kidney', 'creatinine', 'urea', 'egfr', 'почки', 'креатинин', 'мочевина', 'скф', 'buyrak', 'kreatinin', 'mochevina', 'kft'],
    ['liver', 'alt', 'ast', 'печень', 'алт', 'аст', 'jigar'], ['ecg', 'ekg', 'cardiogram', 'экг', 'кардиограмма'],
    ['xray', 'x ray', 'radiograph', 'рентген', 'rentgen'], ['ultrasound', 'узи', 'utt'],
    ['antibodies', 'gad', 'антитела', 'antitanalar'],
    ['lipids', 'cholesterol', 'triglycerides', 'липиды', 'холестерин', 'триглицериды', 'lipidlar', 'xolesterin'],
    ['hba1c', 'glycated haemoglobin', 'гликированный гемоглобин', 'glikirlangan gemoglobin']
];

const normalize = (value) => String(value || '').toLowerCase().replace(/ё/g, 'е').replace(/[‘’ʻʼ`]/g, "'")
    .replace(/[_-]+/g, ' ').replace(/[^\p{L}\p{N}+'%]+/gu, ' ').trim();
const normalizeTokens = (value) => normalize(value).split(/\s+/).filter(Boolean);
const shortSearchTerms = new Set(['bp', 'hr', 'rr', 'o2', 'ur', 'ua', 'ecg', 'ekg', 'cbc', 'fbc', 'ph', 'кт', 'мрт', 'узи', 'кщс', 'оак', 'экг', 'utt', 'kt', 'mrt']);
const shortSearchGroups = {
    ur: [2],
    ua: [2]
};
const phraseIncluded = (text, phrase) => {
    const textTokens = normalizeTokens(text);
    return normalizeTokens(phrase).every(part => textTokens.some(token =>
        token === part || (part.length >= 4 && (token.startsWith(part) || part.startsWith(token)))
    ));
};
const semanticGroupIndexes = value => {
    const text = normalize(value);
    return searchGroups.map((group, index) => group.some(term => phraseIncluded(text, term)) ? index : -1).filter(index => index >= 0);
};
const semanticText = (...values) => {
    const base = normalize(values.filter(Boolean).join(' '));
    const expanded = [base];
    searchGroups.forEach(group => { if (group.some(term => phraseIncluded(base, term))) expanded.push(...group); });
    return normalize(expanded.join(' '));
};

const labelKey = (value) => {
    const normalized = normalize(value);
    return Object.keys(labelAliases).find(key => normalize(key) === normalized || labelAliases[key].some(alias => normalize(alias) === normalized));
};
const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
const translateLabel = (value, language) => {
    const key = labelKey(value);
    return key ? clinicalLabels[key]?.[language] || clinicalLabels[key]?.en : humanize(value);
};

const unavailablePattern = /^(not available|not specified|not done|unavailable|не доступ(?:ен|на|ны)?|не делалось|не указан(?:о|а|ы)?|mavjud emas|bajarilmagan|ko['‘’ʻʼ]?rsatilmagan)$/i;
const isAvailable = (value) => value !== null && value !== undefined && String(value).trim() !== '' && !unavailablePattern.test(String(value).trim());
const unknownLabelNeedsTranslation = (value, language) => {
    if (labelKey(value)) return false;
    const hasCyrillic = /\p{Script=Cyrillic}/u.test(String(value || ''));
    if (language === 'en') return hasCyrillic;
    if (language === 'ru') return !hasCyrillic;
    return true;
};
const resultReplacements = {
    en: [[/Глюкоза мочи/gi, 'Urine glucose'], [/Ацетон в моче/gi, 'Urine ketones'], [/положительный/gi, 'positive'], [/при поступлении/gi, 'on admission'], [/Мочевина/gi, 'Urea'], [/Креатинин/gi, 'Creatinine'], [/Тромбоциты/gi, 'Platelets'], [/Синусовая тахикардия/gi, 'Sinus tachycardia'], [/с ЧСС/gi, 'with heart rate'], [/диффузно-дистрофические изменения/gi, 'diffuse dystrophic changes'], [/Норма/gi, 'Normal'], [/Положительно/gi, 'Positive']],
    uz: [[/Глюкоза мочи/gi, 'Siydik glyukozasi'], [/Ацетон в моче/gi, 'Siydik ketonlari'], [/положительный/gi, 'musbat'], [/при поступлении/gi, 'qabul vaqtida'], [/Мочевина/gi, 'Mochevina'], [/Креатинин/gi, 'Kreatinin'], [/Тромбоциты/gi, 'Trombotsitlar'], [/Синусовая тахикардия/gi, 'Sinusli taxikardiya'], [/с ЧСС/gi, 'yurak urish tezligi'], [/диффузно-дистрофические изменения/gi, 'diffuz distrofik o‘zgarishlar'], [/Норма/gi, 'Me’yorida'], [/Положительно/gi, 'Musbat']],
    ru: [[/Urine glucose/gi, 'Глюкоза мочи'], [/Urine ketones/gi, 'Кетоны мочи'], [/Positive/gi, 'Положительно'], [/Elevated/gi, 'Повышено'], [/High/gi, 'Высокий уровень'], [/Normal/gi, 'Норма']]
};
const translateResult = (value, language) => {
    if (!isAvailable(value)) return { en: 'Not available', ru: 'Нет результата', uz: 'Natija mavjud emas' }[language];
    let output = String(value);
    (resultReplacements[language] || []).forEach(([pattern, replacement]) => { output = output.replace(pattern, replacement); });
    return output;
};

const flattenTests = (value, prefix = '') => {
    if (value === null || value === undefined || typeof value !== 'object' || Array.isArray(value)) {
        return [{ name: prefix || 'Result', result: Array.isArray(value) ? value.join('; ') : value }];
    }
    return Object.entries(value).flatMap(([key, nested]) => {
        const name = prefix ? `${prefix} / ${key}` : key;
        return nested !== null && typeof nested === 'object' && !Array.isArray(nested) ? flattenTests(nested, name) : [{ name, result: Array.isArray(nested) ? nested.join('; ') : nested }];
    });
};
const reportNamespaces = new Set(['lab_reports', 'laboratory_tests', 'investigations', 'diagnostic_tests', 'instrumental_studies']);
const labTranslationCache = new Map();

const persistedTranslation = (localizations, language, value) => {
    const dictionary = localizations?.[language] || {};
    const source = String(value || '');
    return dictionary[source] || dictionary[source.replace(/_/g, ' ')] || '';
};
const persistedAliases = (localizations, value) => {
    const source = String(value || '');
    const humanized = source.replace(/_/g, ' ');
    return [...new Set(['en', 'ru', 'uz'].flatMap(language => {
        const dictionary = localizations?.[language] || {};
        return [dictionary[source], dictionary[humanized]];
    }).filter(Boolean))];
};

export const normalizeLabReports = (source, language = 'en', localizations = {}) => {
    if (!source || typeof source !== 'object') return [];
    const rawCategories = [];
    const addCategory = (category, value) => {
        const tests = flattenTests(value);
        if (tests.length) rawCategories.push({ category, tests });
    };
    Object.entries(source).forEach(([root, value]) => {
        if (reportNamespaces.has(root) && value && typeof value === 'object' && !Array.isArray(value)) {
            const scalar = {};
            Object.entries(value).forEach(([key, nested]) => {
                if (nested !== null && typeof nested === 'object' && !Array.isArray(nested)) addCategory(key, nested);
                else scalar[key] = nested;
            });
            if (Object.keys(scalar).length) addCategory('laboratory', scalar);
        } else addCategory(root, value);
    });
    return rawCategories.map(({ category, tests }) => ({
        category,
        label: persistedTranslation(localizations, language, category) || translateLabel(category, language),
        searchAliases: persistedAliases(localizations, category),
        labelNeedsTranslation: !persistedTranslation(localizations, language, category) && unknownLabelNeedsTranslation(category, language),
        tests: tests.map(({ name, result }) => {
            const available = isAvailable(result);
            const persistedResult = persistedTranslation(localizations, language, result);
            const displayResult = persistedResult || translateResult(result, language);
            const labelParts = name.split(' / ');
            const translatedParts = labelParts.map(part => persistedTranslation(localizations, language, part) || translateLabel(part, language));
            return { name, label: translatedParts.join(' / '), searchAliases: labelParts.flatMap(part => persistedAliases(localizations, part)), labelNeedsTranslation: labelParts.some(part => !persistedTranslation(localizations, language, part) && unknownLabelNeedsTranslation(part, language)), available, result: result == null ? '' : String(result), displayResult, resultPersisted: Boolean(persistedResult), abnormal: /elevated|high|positive|\+\+|low|decreased|tachy|abnormal|полож|низ|выс|тахи|musbat|yuqori|past/i.test(displayResult) };
        })
    }));
};

const containsHumanLanguage = value => /\p{L}{2,}/u.test(String(value || ''));
const resultNeedsTranslation = (value, language) => {
    if (!containsHumanLanguage(value)) return false;
    if (language === 'en') return /\p{Script=Cyrillic}/u.test(String(value));
    return true;
};
const cacheKey = (language, text) => `${language}\u0000${text}`;

export const matchesLabSearch = (category, test, search) => {
    const rawHaystack = [category.category, category.label, ...(category.searchAliases || []), test.name, test.label, ...(test.searchAliases || [])].join(' ');
    const queryGroups = shortSearchGroups[normalize(search)] || semanticGroupIndexes(search);
    const haystackGroups = semanticGroupIndexes(rawHaystack);
    if (queryGroups.length) return queryGroups.every(group => haystackGroups.includes(group));
    const haystack = semanticText(rawHaystack);
    const terms = normalizeTokens(semanticText(search));
    const haystackTokens = normalizeTokens(haystack);
    return terms.length > 0 && terms.every(term => haystackTokens.some(token => token === term || token.startsWith(term)) || haystack.includes(term));
};

const LabReportsPanel = ({ isOpen, onClose, caseData }) => {
    const { language, t } = useLanguage();
    const [expandedCategories, setExpandedCategories] = useState({});
    const [expandedTests, setExpandedTests] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [remoteReports, setRemoteReports] = useState(null);
    const [remoteLocalizations, setRemoteLocalizations] = useState({});
    const [loading, setLoading] = useState(false);
    const [translatedTexts, setTranslatedTexts] = useState({});
    const fallbackLabs = useMemo(() => getCaseProfile(caseData, language).labs || {}, [caseData, language]);

    useEffect(() => {
        setSearchTerm('');
        setExpandedCategories({});
        setExpandedTests({});
        setRemoteReports(null);
        setRemoteLocalizations({});
        setTranslatedTexts({});
    }, [caseData?.id]);
    useEffect(() => {
        if (!isOpen || !caseData?.id) return undefined;
        let cancelled = false;
        setLoading(true);
        apiService.getCaseLabs(caseData.id, language)
            .then(payload => {
                if (cancelled) return;
                setRemoteReports(payload?.reports && Object.keys(payload.reports).length ? payload.reports : null);
                setRemoteLocalizations(payload?.localizations || {});
            })
            .catch(error => { console.error('Using case-specific fallback lab data:', error); if (!cancelled) setRemoteReports(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [isOpen, caseData?.id, language]);

    const labData = useMemo(() => normalizeLabReports(remoteReports || fallbackLabs, language, remoteLocalizations), [remoteReports, fallbackLabs, language, remoteLocalizations]);
    useEffect(() => {
        if (!isOpen || !labData.length) return undefined;
        let cancelled = false;
        const candidates = [...new Set(labData.flatMap(category => [
            ...(category.labelNeedsTranslation ? [category.label] : []),
            ...category.tests.flatMap(test => [
                ...(test.labelNeedsTranslation ? [test.label] : []),
                ...(test.available && !test.resultPersisted && resultNeedsTranslation(test.displayResult, language) ? [test.displayResult] : [])
            ])
        ]).filter(Boolean))];
        const cached = {};
        const missing = [];
        candidates.forEach(text => {
            const translated = labTranslationCache.get(cacheKey(language, text));
            if (translated) cached[text] = translated;
            else missing.push(text);
        });
        setTranslatedTexts(cached);
        if (!missing.length) return () => { cancelled = true; };

        apiService.translateTexts(missing, language, { strict: true })
            .then(payload => {
                const translations = Array.isArray(payload?.data) ? payload.data : [];
                if (cancelled || translations.length !== missing.length) return;
                const next = { ...cached };
                missing.forEach((text, index) => {
                    const translated = String(translations[index] || text).trim();
                    labTranslationCache.set(cacheKey(language, text), translated);
                    next[text] = translated;
                });
                setTranslatedTexts(next);
            })
            .catch(error => console.error('Lab result translation failed; using deterministic fallback:', error));
        return () => { cancelled = true; };
    }, [isOpen, labData, language]);

    const localizedLabData = useMemo(() => labData.map(category => ({
        ...category,
        label: translatedTexts[category.label] || category.label,
        tests: category.tests.map(test => ({
            ...test,
            label: translatedTexts[test.label] || test.label,
            displayResult: translatedTexts[test.displayResult] || test.displayResult
        }))
    })), [labData, translatedTexts]);
    const normalizedSearch = normalize(searchTerm);
    const searchReady = normalizedSearch.length >= 2 || shortSearchTerms.has(normalizedSearch);
    const visibleLabData = searchReady ? localizedLabData.map(category => ({ ...category, tests: category.tests.filter(test => matchesLabSearch(category, test, normalizedSearch)) })).filter(category => category.tests.length) : [];
    if (!isOpen) return null;

    const toggleCategory = category => setExpandedCategories(prev => ({ ...prev, [category]: !(prev[category] ?? true) }));
    const toggleTest = testKey => setExpandedTests(prev => ({ ...prev, [testKey]: !prev[testKey] }));
    return (
        <div className="lab-reports-panel">
            <div className="lab-panel-header"><div><h3>{t('labReports')}</h3><p>{t('labSearchHelp')}</p></div><button className="close-panel-btn" onClick={onClose}><FiX size={20} /></button></div>
            <div className="lab-panel-content">
                <div className="lab-search-box"><FiSearch size={18} /><input value={searchTerm} onChange={event => setSearchTerm(event.target.value)} placeholder={t('labSearchPlaceholder')} /></div>
                {!normalizedSearch && <p className="lab-empty-hint">{loading ? t('loadingLabs') : t('labTypeHint')}</p>}
                {normalizedSearch && !searchReady && <p className="lab-empty-hint">{t('labSearchMore')}</p>}
                {searchReady && visibleLabData.length === 0 && <p className="lab-empty-hint">{t('noMatchingReport')}</p>}
                {visibleLabData.map(category => {
                    const categoryExpanded = expandedCategories[category.category] ?? true;
                    return <div key={category.category} className="lab-category">
                        <button className="category-header" onClick={() => toggleCategory(category.category)}>{categoryExpanded ? <FiChevronDown size={18} /> : <FiChevronRight size={18} />}<span className="category-name">{category.label}</span><span className="test-count">{category.tests.length}</span></button>
                        {categoryExpanded && <div className="category-tests">{category.tests.map(test => {
                            const testKey = `${category.category}-${test.name}`;
                            const isExpanded = expandedTests[testKey];
                            return <div key={testKey} className="test-item">
                                <button className={`test-header ${!test.available ? 'unavailable' : ''}`} onClick={() => test.available && toggleTest(testKey)} disabled={!test.available}>
                                    <div className="test-status">{test.available ? <FiCheck className="status-icon available" size={16} /> : <FiX className="status-icon unavailable" size={16} />}</div><span className="test-name">{test.label}</span>{test.available && (isExpanded ? <FiChevronDown className="expand-icon" size={14} /> : <FiChevronRight className="expand-icon" size={14} />)}
                                </button>
                                {test.available && isExpanded && <div className="test-details"><div className={`result-row ${test.abnormal ? 'abnormal' : 'normal'}`}><span className="result-label">{t('result')}:</span><span className="result-value">{test.displayResult}</span></div></div>}
                            </div>;
                        })}</div>}
                    </div>;
                })}
            </div>
        </div>
    );
};

export default LabReportsPanel;
